import { useContext, useEffect, useMemo, useState } from 'react';
import { PatternFormat } from 'react-number-format';
import AnimatedComponent from '../Components/AnimatedComponent';
import Button from '../Components/Button';
import { LanguageContext } from '../Components/LanguageContext';
import MobileHeroDiagonal from '../Components/MobileHeroDiagonal';
import NavigationBar from '../Components/NavigationBar';
import SocialMediaBanner from '../Components/SocialMediaBanner';
import Layout from '../Layout';
import { fetchBankAccounts, postContact } from '../api/publicApi';
import handleTransformLang from '../functions/handleTransformLang';
import { toast } from '../lib/toast';
import NavLang from '../lang/pages/nav.json';
import contact from '../lang/pages/contact.json';
import Background from '../public/images/bg_main.svg';

const CONTACT_INPUT_CLASS =
  'block w-full border-0 bg-white px-3 py-2.5 text-[17px] text-text_secondary ring-1 ring-[#b8bec8] transition placeholder:text-[#bcc3cf] focus:outline-none focus:ring-2 focus:ring-text_primary min-[701px]:px-3 min-[701px]:py-3 min-[701px]:text-[17px] xl:text-[18px]';
const CONTACT_DEPARTMENT_KEYS = [
  'management',
  'sales',
  'marketing',
  'technicalSupport',
  'webmarket',
  'finance',
  'warehouse',
];

function createInitialDepartmentState() {
  return CONTACT_DEPARTMENT_KEYS.reduce((state, key) => {
    state[key] = false;
    return state;
  }, {});
}

function createInitialContactFormState() {
  return {
    companyName: '',
    fullName: '',
    email: '',
    phone: '',
    message: '',
    sendTo: createInitialDepartmentState(),
  };
}

function normalizeText(value) {
  return String(value ?? '').trim();
}

function normalizeDigits(value) {
  return normalizeText(value).replace(/\D/g, '');
}

function formatTemplate(template, values = {}) {
  return Object.entries(values).reduce(
    (message, [key, value]) => message.replace(`{${key}}`, String(value ?? '')),
    String(template ?? '')
  );
}

function withErrorClass(baseClassName, hasError) {
  return hasError
    ? `${baseClassName} ring-2 ring-red-500 focus:ring-red-500`
    : baseClassName;
}

function scrollToFirstError(errors, fieldOrder) {
  if (typeof document === 'undefined') {
    return;
  }

  const firstKey = fieldOrder.find((fieldName) => errors[fieldName]);

  if (!firstKey) {
    return;
  }

  requestAnimationFrame(() => {
    document
      .querySelector(`[data-error-field="${firstKey}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  });
}

function buildContactPayload(formData, departments, pageContent) {
  const from = [normalizeText(formData.fullName), normalizeText(formData.companyName)]
    .filter(Boolean)
    .join(' / ') || normalizeText(formData.email) || pageContent.formSection?.subject;

  const selectedDepartments = departments
    .filter((department) => formData.sendTo?.[department.key])
    .map((department) => department.label);

  const messageParts = [
    `${pageContent.formSection?.fields?.fullName}: ${normalizeText(formData.fullName)}`,
    `${pageContent.formSection?.fields?.companyName}: ${normalizeText(formData.companyName)}`,
    `${pageContent.formSection?.fields?.email}: ${normalizeText(formData.email)}`,
    `${pageContent.formSection?.fields?.phone}: ${normalizeText(formData.phone)}`,
  ];

  if (selectedDepartments.length) {
    messageParts.push(
      `${pageContent.formSection?.fields?.department}: ${selectedDepartments.join(', ')}`
    );
  }

  return {
    from,
    subject: normalizeText(pageContent.formSection?.subject),
    message: `${messageParts.join('\n')}\n\n${normalizeText(formData.message)}`,
    sendTo: { ...createInitialDepartmentState(), ...formData.sendTo },
  };
}

function resolveSubmitErrorMessage(error, fallbackMessage) {
  const message = normalizeText(error?.message);

  if (
    error?.name === 'AbortError' ||
    !message ||
    message === 'Failed to fetch' ||
    /^Request failed with status \d+$/.test(message)
  ) {
    return fallbackMessage;
  }

  return message;
}

export default function Contact() {
  return (
    <Layout headData={contact.head}>
      <InnerPage />
    </Layout>
  );
}

function InnerPage() {
  const { language } = useContext(LanguageContext);

  const [content, setContent] = useState(handleTransformLang(NavLang));
  const [pageContent, setPageContent] = useState(handleTransformLang(contact));
  const [bankAccounts, setBankAccounts] = useState([]);
  const [bankAccountsLoading, setBankAccountsLoading] = useState(true);
  const [bankAccountsError, setBankAccountsError] = useState(false);
  const [selectedBankIndex, setSelectedBankIndex] = useState(0);
  const [showMobileBankList, setShowMobileBankList] = useState(false);
  const [formData, setFormData] = useState(createInitialContactFormState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitState, setSubmitState] = useState('idle');
  const [submitMessage, setSubmitMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    let mounted = true;

    async function loadBankAccounts() {
      setBankAccountsLoading(true);
      setBankAccountsError(false);

      try {
        const response = await fetchBankAccounts();
        if (!mounted) return;
        setBankAccounts(Array.isArray(response) ? response : []);
      } catch (error) {
        if (!mounted) return;
        console.log(error);
        setBankAccounts([]);
        setBankAccountsError(true);
      } finally {
        if (mounted) setBankAccountsLoading(false);
      }
    }

    setContent(handleTransformLang(NavLang));
    setPageContent(handleTransformLang(contact));
    loadBankAccounts();

    return () => {
      mounted = false;
    };
  }, [language]);

  useEffect(() => {
    setSelectedBankIndex(0);
    setShowMobileBankList(false);
  }, [bankAccounts]);

  const addresses = useMemo(() => Object.values(pageContent.addressSection || {}), [pageContent.addressSection]);
  const selectedBank = bankAccounts[selectedBankIndex] || null;
  const heroImageSrc = pageContent.mainSection?.image?.startsWith('/') ? pageContent.mainSection.image : `/${pageContent.mainSection?.image || ''}`;
  const bankTableHeaders = pageContent.bankSection?.tableHeaders || [];
  const formFields = pageContent.formSection?.fields || {};
  const departments = pageContent.formSection?.departments || [];
  const validationFieldOrder = [
    'companyName',
    'fullName',
    'email',
    'phone',
    'department',
    'message',
  ];

  function clearSubmitFeedback() {
    setSubmitState('idle');
    setSubmitMessage('');
  }

  function clearFieldError(fieldName) {
    setFieldErrors((currentErrors) => {
      if (!currentErrors[fieldName]) {
        return currentErrors;
      }

      const nextErrors = { ...currentErrors };
      delete nextErrors[fieldName];
      return nextErrors;
    });
  }

  function updateField(name, value) {
    clearSubmitFeedback();
    clearFieldError(name);
    setFormData((currentData) => ({
      ...currentData,
      [name]: value,
    }));
  }

  function toggleDepartment(key) {
    clearSubmitFeedback();
    clearFieldError('department');
    setFormData((currentData) => ({
      ...currentData,
      sendTo: {
        ...currentData.sendTo,
        [key]: !currentData.sendTo[key],
      },
    }));
  }

  function validateForm() {
    const errors = {};

    if (!normalizeText(formData.companyName)) {
      errors.companyName = formatTemplate(pageContent.formSection?.requiredField, {
        field: formFields.companyName,
      });
    }

    if (!normalizeText(formData.fullName)) {
      errors.fullName = formatTemplate(pageContent.formSection?.requiredField, {
        field: formFields.fullName,
      });
    }

    if (!normalizeText(formData.email)) {
      errors.email = formatTemplate(pageContent.formSection?.requiredField, {
        field: formFields.email,
      });
    }

    if (!normalizeDigits(formData.phone) || normalizeText(formData.phone).includes('_')) {
      errors.phone = formatTemplate(pageContent.formSection?.requiredField, {
        field: formFields.phone,
      });
    }

    if (!Object.values(formData.sendTo || {}).some(Boolean)) {
      errors.department = pageContent.formSection?.departmentRequired;
    }

    if (!normalizeText(formData.message)) {
      errors.message = formatTemplate(pageContent.formSection?.requiredField, {
        field: formFields.message,
      });
    }

    return errors;
  }

  async function handleSubmit(event) {
    event.preventDefault();
    clearSubmitFeedback();
    const validationErrors = validateForm();

    if (Object.keys(validationErrors).length > 0) {
      setFieldErrors(validationErrors);
      scrollToFirstError(validationErrors, validationFieldOrder);
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = buildContactPayload(formData, departments, pageContent);
      await postContact(payload);
      const successMessage = pageContent.formSection?.success;

      setFormData(createInitialContactFormState());
      setFieldErrors({});
      setSubmitState('success');
      setSubmitMessage(successMessage);
      toast.success(successMessage);
    } catch (error) {
      console.error(error);
      const errorMessage = resolveSubmitErrorMessage(
        error,
        pageContent.formSection?.error
      );

      setSubmitState('error');
      setSubmitMessage(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <>
      <section className="relative overflow-hidden bg-white pb-12 sm:pb-16 1.5md:pb-20 xl:pb-24 1.5xl:pb-18">
        <div
          className="absolute inset-0 hidden bg-no-repeat sm:block sm:bg-[length:1180px_480px] sm:bg-top 1.5md:bg-[length:1560px_620px] 1.5md:bg-top xl:bg-[length:1760px_700px] xl:bg-top 1.5xl:bg-[length:1915px_750px] 1.5xl:bg-top"
          style={{ backgroundImage: `url(${Background})` }}
        />
        <MobileHeroDiagonal className="top-[11.25rem] h-[16.5rem] w-[12.6rem] sm:top-[5rem]" />
        <div className="h-1.5 w-full bg-bg_secondary" />

        <div className="relative z-20 container mx-auto max-w-8xl px-5 py-5 sm:px-4 sm:py-7 1.5md:py-9 1.5xl:py-11">
          <NavigationBar
            content={content}
            desktopNavBreakpoint="1.5xl"
            logoClassName="h-16 sm:h-18 1.5md:h-20 1.5xl:h-25"
            b2bButtonClassName="px-4 py-[0.45rem] text-[13px] 1.5md:px-4 1.5md:py-[0.45rem] 1.5md:text-[14px] 1.5xl:px-5 1.5xl:py-2 1.5xl:text-lg"
            hamburgerIconClassName="h-9 w-9 sm:h-10 sm:w-10 1.5md:h-11 1.5md:w-11"
          />

          <div className="grid items-start gap-5 pt-1 sm:gap-6 sm:pt-3 min-[701px]:grid-cols-[0.62fr_1fr] min-[701px]:gap-8 min-[701px]:pt-7 xl:grid-cols-[0.58fr_1.03fr] xl:gap-10 xl:pt-10 1.5xl:grid-cols-2 1.5xl:gap-16 1.5xl:pt-10">
            <AnimatedComponent
              direction="animate-fade-right"
              className="order-2 mx-auto flex max-w-[18rem] flex-col text-center min-[701px]:order-1 min-[701px]:mx-0 min-[701px]:max-w-[18.5rem] min-[701px]:pt-20 min-[701px]:ps-3 min-[701px]:text-left xl:max-w-[22rem] xl:pt-16 xl:ps-8 1.5xl:max-w-none 1.5xl:pe-4 1.5xl:pt-16 1.5xl:ps-0"
            >
              <HeroInfoBlock title={pageContent.mainSection?.header1} centered>
                <p>{pageContent.mainSection?.text1_1}</p>
                <div className="mt-1 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 min-[701px]:justify-start">
                  <span>{pageContent.mainSection?.text1_2}</span>
                  <span className="hidden text-text_primary/50 min-[701px]:inline">|</span>
                  <span>{pageContent.mainSection?.text1_3}</span>
                </div>
              </HeroInfoBlock>

              <HeroInfoBlock title={pageContent.mainSection?.header2} centered className="mt-7 min-[701px]:mt-8">
                <p>{pageContent.mainSection?.text2_1}</p>
                <div className="mt-1 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 min-[701px]:justify-start">
                  <span>{pageContent.mainSection?.text2_2}</span>
                  <span className="hidden text-text_primary/50 min-[701px]:inline">|</span>
                  <span>{pageContent.mainSection?.text2_3}</span>
                </div>
              </HeroInfoBlock>
            </AnimatedComponent>

            <AnimatedComponent
              direction="animate-fade-left"
              className="order-1 flex justify-center min-[701px]:order-2 min-[701px]:justify-end"
            >
              <img
                src={heroImageSrc}
                alt=""
                className="relative z-10 mx-auto mt-[2.25rem] w-full max-w-[19rem] object-contain sm:mt-[3.5rem] min-[701px]:mt-0 min-[701px]:max-w-[22rem] min-[701px]:translate-x-[6px] min-[701px]:translate-y-[40px] xl:max-w-[27rem] xl:translate-x-[8px] xl:translate-y-[72px] 1.5xl:h-125 1.5xl:max-w-none 1.5xl:translate-x-0 1.5xl:translate-y-0 1.5xl:pe-16"
              />
            </AnimatedComponent>
          </div>
        </div>
      </section>

      <section className="bg-white pb-14 pt-6 sm:pt-8 min-[701px]:py-16 xl:py-18 1.5xl:py-20">
        <div className="container mx-auto max-w-8xl px-4">
          <div className="grid min-[701px]:grid-cols-2">
            {addresses.map((addressItem, index) => (
              <AnimatedComponent
                key={`${addressItem?.title}-${index}`}
                direction="animate-fade-up animate-duration-500"
                className={getAddressCardClassName(index, addresses.length)}
              >
                <h2 className="text-[26px] font-semibold leading-none text-text_tertiary min-[701px]:text-[28px] 1.5xl:text-3xl">
                  {addressItem.title}
                </h2>
                <p className="mt-4 text-[17px] leading-7 text-text_secondary min-[701px]:text-[17px] xl:text-[18px] xl:leading-8 1.5xl:text-lg">
                  T: {addressItem.telephone}
                </p>
                <p className="mt-1 max-w-[26rem] text-[17px] leading-7 text-text_secondary min-[701px]:text-[17px] xl:text-[18px] xl:leading-8 1.5xl:max-w-none 1.5xl:text-lg">
                  A: {addressItem.address}
                </p>
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={addressItem.location}
                  className="mt-3 inline-flex w-fit items-center gap-2 text-[17px] font-semibold text-text_tertiary transition-colors duration-200 hover:text-text_primary xl:text-[18px] 1.5xl:text-lg 1.5xl:text-text_primary 1.5xl:underline 1.5xl:underline-offset-4 1.5xl:decoration-text_tertiary 1.5xl:hover:text-text_tertiary"
                >
                  <img
                    className="h-[1.15rem] w-[1.15rem] object-contain"
                    src="/images/harita_icon.svg"
                    alt=""
                  />
                  <span>{pageContent.addressMapLabel}</span>
                </a>
              </AnimatedComponent>
            ))}
          </div>
        </div>
      </section>

      <section id="bank" className="bg-bg_tertiary py-14 sm:py-16 min-[701px]:py-18 xl:py-20">
        <div className="container mx-auto max-w-8xl px-4">
          <AnimatedComponent
            direction="animate-fade-up animate-duration-500"
            className="mx-auto max-w-[78rem]"
          >
            <h2 className="text-center text-[24px] font-bold leading-tight text-text_primary sm:text-[26px] min-[701px]:text-[32px] 1.5xl:text-left 1.5xl:text-4xl">
              {pageContent.bankSection?.title}
            </h2>

            <div className="mt-8 hidden min-[701px]:block xl:mt-10">
              <div className="overflow-hidden">
                <table className="w-full table-auto">
                  <thead className="border-b border-[#d2d4d9] text-[17px] font-bold text-text_tertiary">
                    <tr>
                      {bankTableHeaders.map((header) => (
                        <th key={header} className="py-3 pe-3 text-left last:pe-0">
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="text-[17px] text-text_secondary">
                    {bankAccountsLoading ? (
                      <tr className="border-b border-[#d2d4d9]">
                        <td className="py-6" colSpan={bankTableHeaders.length}>
                          {pageContent.bankSection?.states?.loading}
                        </td>
                      </tr>
                    ) : bankAccountsError ? (
                      <tr className="border-b border-[#d2d4d9]">
                        <td className="py-6" colSpan={bankTableHeaders.length}>
                          {pageContent.bankSection?.states?.error}
                        </td>
                      </tr>
                    ) : bankAccounts.length === 0 ? (
                      <tr className="border-b border-[#d2d4d9]">
                        <td className="py-6" colSpan={bankTableHeaders.length}>
                          {pageContent.bankSection?.states?.empty}
                        </td>
                      </tr>
                    ) : (
                      bankAccounts.map((bankAccount, index) => (
                        <tr
                          key={`${getBankLabel(bankAccount, index)}-${bankAccount?.iban || index}`}
                          className="border-b border-[#d2d4d9]"
                        >
                          <td className="py-3 pe-3">{bankAccount?.bankName || '-'}</td>
                          <td className="py-3 pe-3">{bankAccount?.branchName || '-'}</td>
                          <td className="py-3 pe-3">{bankAccount?.branchCode || '-'}</td>
                          <td className="py-3 pe-3">{bankAccount?.currencyType || '-'}</td>
                          <td className="py-3 pe-3">{bankAccount?.accountNo || '-'}</td>
                          <td className="py-3">{bankAccount?.iban || '-'}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mt-8 min-[701px]:hidden">
              {bankAccountsLoading ? (
                <MobileBankMessage message={pageContent.bankSection?.states?.loading} />
              ) : bankAccountsError ? (
                <MobileBankMessage message={pageContent.bankSection?.states?.error} />
              ) : bankAccounts.length === 0 ? (
                <MobileBankMessage message={pageContent.bankSection?.states?.empty} />
              ) : (
                <>
                  <div className="overflow-hidden border border-[#cfd3d9] bg-white">
                    <button
                      type="button"
                      onClick={() => setShowMobileBankList((previousState) => !previousState)}
                      className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left"
                    >
                      <span className="text-[17px] font-bold text-text_tertiary">{pageContent.bankSection?.mobileSelectLabel}</span>
                      <svg
                        className={`h-4 w-4 text-text_tertiary transition-transform duration-200 ${showMobileBankList ? 'rotate-180' : ''}`}
                        viewBox="0 0 16 16"
                        fill="currentColor"
                      >
                        <path d="M3 5.75 8 10.5l5-4.75-.9-.95L8 8.7 3.9 4.8z" />
                      </svg>
                    </button>

                    {showMobileBankList && (
                      <div className="border-t border-[#cfd3d9] bg-[#727887]">
                        {bankAccounts.map((bankAccount, index) => {
                          const isSelected = selectedBankIndex === index;
                          return (
                            <button
                              key={`${getBankLabel(bankAccount, index)}-${bankAccount?.iban || index}`}
                              type="button"
                              onClick={() => {
                                setSelectedBankIndex(index);
                                setShowMobileBankList(false);
                              }}
                              className={`block w-full border-b border-white/15 px-3 py-2.5 text-left text-[17px] font-medium uppercase tracking-[0.02em] transition-colors duration-200 last:border-b-0 ${
                                isSelected ? 'bg-[#5f6573] text-white' : 'text-white/96 hover:bg-[#676d7a]'
                              }`}
                            >
                              {getBankLabel(bankAccount, index, pageContent.bankSection?.fallbackLabel)}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div className="mt-5 space-y-2.5">
                    <MobileBankRow label={bankTableHeaders[0]} value={selectedBank?.bankName || '-'} />
                    <MobileBankRow label={bankTableHeaders[1]} value={selectedBank?.branchName || '-'} />
                    <MobileBankRow label={bankTableHeaders[2]} value={selectedBank?.branchCode || '-'} />
                    <MobileBankRow label={bankTableHeaders[3]} value={selectedBank?.currencyType || '-'} />
                    <MobileBankRow label={bankTableHeaders[4]} value={selectedBank?.accountNo || '-'} />
                    <MobileBankRow label={bankTableHeaders[5]} value={selectedBank?.iban || '-'} />
                  </div>
                </>
              )}
            </div>
          </AnimatedComponent>
        </div>
      </section>

      <section className="flex flex-col items-center justify-center bg-bg_primary text-center">
        <div className="mx-auto flex w-full max-w-8xl flex-col items-center justify-center px-4">
          <SocialMediaBanner />
        </div>
      </section>

      <section className="bg-white pb-20 pt-12 sm:pt-14 min-[701px]:py-18 xl:py-20">
        <div className="container mx-auto max-w-8xl px-4">
          <AnimatedComponent
            direction="animate-fade-up animate-duration-500"
            className="mx-auto max-w-[72rem]"
          >
            <div className="text-center">
              <h2 className="text-[24px] font-bold leading-tight text-text_primary sm:text-[26px] min-[701px]:text-[32px] 1.5xl:text-4xl">
                {pageContent.formSection?.title}
              </h2>
            </div>

            <form
              className="mx-auto mt-9 max-w-[19.5rem] min-[701px]:mt-12 min-[701px]:max-w-[63rem] xl:max-w-[68rem] 1.5xl:max-w-7xl 1.5xl:pe-25"
              onSubmit={handleSubmit}
              noValidate
            >
              <div className="grid gap-x-10 gap-y-4 min-[701px]:gap-y-8 lg:grid-cols-2 xl:gap-x-12">
                <ContactField
                  label={formFields.companyName}
                  fieldKey="companyName"
                  error={fieldErrors.companyName}
                >
                  <input
                    id="company"
                    type="text"
                    maxLength={80}
                    required
                    value={formData.companyName}
                    onChange={(event) => updateField('companyName', event.target.value)}
                    placeholder={formFields.companyName}
                    className={`${withErrorClass(CONTACT_INPUT_CLASS, Boolean(fieldErrors.companyName))} 1.5xl:placeholder:opacity-0`}
                  />
                </ContactField>

                <ContactField
                  label={formFields.fullName}
                  fieldKey="fullName"
                  error={fieldErrors.fullName}
                >
                  <input
                    id="name"
                    type="text"
                    maxLength={80}
                    required
                    value={formData.fullName}
                    onChange={(event) => updateField('fullName', event.target.value)}
                    placeholder={formFields.fullName}
                    className={`${withErrorClass(CONTACT_INPUT_CLASS, Boolean(fieldErrors.fullName))} 1.5xl:placeholder:opacity-0`}
                  />
                </ContactField>

                <ContactField
                  label={formFields.email}
                  fieldKey="email"
                  error={fieldErrors.email}
                >
                  <input
                    id="email"
                    type="email"
                    maxLength={80}
                    required
                    value={formData.email}
                    onChange={(event) => updateField('email', event.target.value)}
                    placeholder={formFields.email}
                    className={`${withErrorClass(CONTACT_INPUT_CLASS, Boolean(fieldErrors.email))} 1.5xl:placeholder:opacity-0`}
                  />
                </ContactField>

                <ContactField
                  label={formFields.phone}
                  fieldKey="phone"
                  error={fieldErrors.phone}
                >
                  <PatternFormat
                    format="0 (###) ### ## ##"
                    mask="_"
                    required
                    value={formData.phone}
                    onValueChange={({ formattedValue }) => updateField('phone', formattedValue)}
                    placeholder={formFields.phone}
                    className={`${withErrorClass(CONTACT_INPUT_CLASS, Boolean(fieldErrors.phone))} 1.5xl:placeholder:opacity-0`}
                  />
                </ContactField>
              </div>

              <div
                data-error-field="department"
                className="mt-6 min-[701px]:mt-8 min-[701px]:grid min-[701px]:grid-cols-[130px_1fr] min-[701px]:gap-4"
              >
                <div className="text-left text-[17px] font-semibold text-text_secondary min-[701px]:pt-1 min-[701px]:text-right min-[701px]:text-[17px] xl:text-[18px]">
                  {formFields.department}
                </div>

                <div className="space-y-1">
                  <div className={`mt-3 grid grid-cols-2 gap-x-4 gap-y-4 text-[17px] text-text_secondary min-[701px]:mt-0 min-[701px]:grid-cols-4 min-[701px]:gap-x-6 min-[701px]:gap-y-5 min-[701px]:text-[17px] xl:text-[18px] ${fieldErrors.department ? 'rounded-md ring-1 ring-red-500 p-3' : ''}`}>
                    {departments.map((item) => (
                      <label key={item.key} className="inline-flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={Boolean(formData.sendTo[item.key])}
                          onChange={() => toggleDepartment(item.key)}
                          className="h-4 w-4 shrink-0 rounded-none border border-[#b1b7c0] text-bg_primary focus:ring-0"
                        />
                        <span>{item.label}</span>
                      </label>
                    ))}
                  </div>

                  {fieldErrors.department ? (
                    <p className="text-xs text-red-600">{fieldErrors.department}</p>
                  ) : null}
                </div>
              </div>

              <div
                data-error-field="message"
                className="mt-6 min-[701px]:mt-8 min-[701px]:ps-[134px]"
              >
                <textarea
                  id="message"
                  maxLength={500}
                  rows={7}
                  required
                  value={formData.message}
                  onChange={(event) => updateField('message', event.target.value)}
                  className={`${withErrorClass(CONTACT_INPUT_CLASS, Boolean(fieldErrors.message))} min-h-[9.5rem] resize-none px-3 py-3 min-[701px]:min-h-[11.5rem] min-[701px]:px-4 min-[701px]:py-4`}
                  placeholder={formFields.messagePlaceholder}
                />

                {fieldErrors.message ? (
                  <p className="mt-1 text-xs text-red-600">{fieldErrors.message}</p>
                ) : null}

                <div className="mt-4 text-[17px] leading-8 text-text_secondary min-[701px]:text-[17px] min-[701px]:leading-8 xl:text-[18px] 1.5xl:text-sm">
                  <p>{pageContent.formSection?.noteTitle}</p>
                  <p>{pageContent.formSection?.noteText}</p>
                </div>

                <div className="mt-6 flex justify-center min-[701px]:mt-8 min-[701px]:justify-end">
                  <div className="flex flex-col items-center gap-4 min-[701px]:items-end">
                    <Button type="submit" disabled={isSubmitting}>
                      {isSubmitting
                        ? pageContent.formSection?.submitting
                        : pageContent.formSection?.submit}
                    </Button>

                    {submitMessage ? (
                      <p
                        className={`text-center text-[15px] leading-6 min-[701px]:text-right ${
                          submitState === 'success'
                            ? 'text-[#0a7a46]'
                            : submitState === 'error'
                              ? 'text-[#c14a2a]'
                              : 'text-text_secondary'
                        }`}
                      >
                        {submitMessage}
                      </p>
                    ) : null}
                  </div>
                </div>
              </div>
            </form>
          </AnimatedComponent>
        </div>
      </section>
    </>
  );
}

function HeroInfoBlock({ title, children, className = '', centered = false }) {
  return (
    <div className={className}>
      <h1 className={`text-[17px] font-semibold text-text_tertiary min-[701px]:text-[17px] xl:text-[18px] 1.5xl:mb-8 1.5xl:text-3xl ${centered ? 'text-center min-[701px]:text-left' : ''}`}>
        {title}
      </h1>
      <div className="mt-3 text-[17px] leading-8 text-text_secondary min-[701px]:text-[17px] xl:text-[18px] xl:leading-8 1.5xl:mt-0 1.5xl:text-lg">
        {children}
      </div>
    </div>
  );
}

function ContactField({ label, fieldKey, error, children }) {
  return (
    <div
      data-error-field={fieldKey}
      className="grid gap-2 min-[701px]:grid-cols-[130px_1fr] min-[701px]:items-center min-[701px]:gap-3"
    >
      <label className="hidden pe-4 text-right text-[13px] font-semibold leading-tight text-text_secondary min-[701px]:block xl:text-[14px] 1.5xl:text-lg">
        {label}
      </label>
      <div className="space-y-1">
        {children}
        {error ? <p className="text-xs text-red-600">{error}</p> : null}
      </div>
    </div>
  );
}

function MobileBankRow({ label, value }) {
  return (
    <div className="grid grid-cols-[6.9rem_1fr] items-start gap-2">
      <div className="text-[17px] font-bold text-text_tertiary">{label}</div>
      <div className="text-[17px] leading-7 text-text_secondary">{value}</div>
    </div>
  );
}

function MobileBankMessage({ message }) {
  return (
    <div className="rounded-sm border border-[#cfd3d9] bg-white px-4 py-5 text-[17px] leading-7 text-text_secondary">
      {message}
    </div>
  );
}

function getAddressCardClassName(index, length) {
  const classes = ['flex', 'flex-col', 'py-7', 'border-[#dddddd]', 'min-[701px]:py-10', 'xl:py-11'];

  if (index !== length - 1) {
    classes.push('border-b');
  }

  if (index < 2) {
    classes.push('min-[701px]:border-b');
  }

  if (index % 2 === 0) {
    classes.push('min-[701px]:border-r', 'min-[701px]:pe-8', 'xl:pe-12');
  } else {
    classes.push('min-[701px]:ps-8', 'xl:ps-12');
  }

  if (index >= 2) {
    classes.push('min-[701px]:border-b-0');
  }

  return classes.join(' ');
}

function getBankLabel(bankAccount, index, fallbackLabel = 'Account') {
  const bankName = bankAccount?.bankName?.trim();
  const currencyType = bankAccount?.currencyType?.trim();

  if (bankName && currencyType) {
    return `${bankName}-${currencyType}`;
  }

  return bankName || bankAccount?.branchName || `${fallbackLabel} ${index + 1}`;
}
